/**
 * useConsensusGlow — per-side render values for the Unabated consensus
 * trend treatments (HYDRA_API.md "Unabated consensus update", 2026-07-05):
 *
 *   A. recency  — number brightness: full on frame arrival (tenth-only
 *                 frames count), decaying back to baseline over ~10s.
 *   B. direction/strength — up/down arrow whose brightness is the
 *                 recency-weighted delta mass over a trailing ~10s window.
 *   C. sharpBookUsed — the sharp-anchor flag for distinct styling.
 *
 * Takes the bucket's `ConsensusTrendState` (from `useConsensusTrendFor`
 * on the WS path or the odds-stream hub's `consensusTrends` on the SSE
 * path) and re-renders the caller on the shared 4 Hz ticker — but ONLY
 * while the state is still visibly decaying, so a quiet dashboard of 100
 * rows pays nothing. New frames re-trigger via the trend reference change;
 * the decay ride-down re-triggers via the conditional tick subscription.
 */

import { useEffect, useReducer } from 'react';
import { subscribeFastTick } from '@/lib/infra/fastTick';
import {
  isConsensusTrendHot,
  readConsensusSideGlow,
  type ConsensusSideGlow,
  type ConsensusTrendState,
} from '@/lib/consensusTrend';

export type { ConsensusSideGlow };

export interface ConsensusGlow {
  away: ConsensusSideGlow;
  home: ConsensusSideGlow;
  draw: ConsensusSideGlow;
  /** Latest sharp-anchor flag; null = unknown (field absent upstream). */
  sharpBookUsed: boolean | null;
}

const bumpReducer = (n: number) => n + 1;

export function useConsensusGlow(trend: ConsensusTrendState | null): ConsensusGlow | null {
  const nowMs = Date.now();
  const hot = trend != null && isConsensusTrendHot(trend, nowMs);
  const [, bump] = useReducer(bumpReducer, 0);
  useEffect(() => {
    if (!hot) return;
    return subscribeFastTick(bump);
    // `trend` in the deps restarts the subscription when a new frame lands
    // while already hot — harmless Set churn, keeps the logic obvious.
  }, [hot, trend]);
  if (trend == null) return null;
  return {
    away: readConsensusSideGlow(trend, 'away', nowMs),
    home: readConsensusSideGlow(trend, 'home', nowMs),
    draw: readConsensusSideGlow(trend, 'draw', nowMs),
    sharpBookUsed: trend.sharpBookUsed,
  };
}
